import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  TrendingDown,
  TrendingUp,
  Warning,
  CheckCircle,
  Info,
} from '@mui/icons-material';
import {departmentStockService} from '../../services/departmentStockService';
import { useSnackbar } from 'notistack';

export const DepartmentStockView = ({ departmentId }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [editFormData, setEditFormData] = useState({
    minimumLevel: 0,
    maximumLevel: 0,
  });
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (departmentId) {
      fetchDepartmentStock();
    }
  }, [departmentId]);

  const fetchDepartmentStock = async () => {
    try {
      setLoading(true);
      const response = await departmentStockService.getDepartmentStockByDept(departmentId);
      // The API returns data in response.data.data.stocks format
      const stocksData = response.data?.data?.stocks || response.data?.stocks || [];
      setStocks(Array.isArray(stocksData) ? stocksData : []);
    } catch (error) {
      enqueueSnackbar('Error fetching department stock', { variant: 'error' });
      console.error('Error fetching department stock:', error);
      setStocks([]); // Ensure stocks is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (stock) => {
    setSelectedStock(stock);
    setEditFormData({
      minimumLevel: stock.minimumLevel || 0,
      maximumLevel: stock.maximumLevel || 0,
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedStock(null);
    setEditFormData({
      minimumLevel: 0,
      maximumLevel: 0,
    });
  };

  const handleEditSave = async () => {
    try {
      await departmentStockService.updateStockLevels(selectedStock._id, editFormData);
      enqueueSnackbar('Stock levels updated successfully', { variant: 'success' });
      handleEditClose();
      fetchDepartmentStock();
    } catch (error) {
      enqueueSnackbar('Error updating stock levels', { variant: 'error' });
    }
  };

  const getStockStatus = (stock) => {
    if (stock.quantity === 0) {
      return { label: 'Depleted', color: 'error', icon: <Warning /> };
    }
    if (stock.minimumLevel && stock.quantity <= stock.minimumLevel) {
      return { label: 'Low Stock', color: 'warning', icon: <TrendingDown /> };
    }
    if (stock.maximumLevel && stock.quantity >= stock.maximumLevel) {
      return { label: 'Overstocked', color: 'info', icon: <TrendingUp /> };
    }
    return { label: 'Normal', color: 'success', icon: <CheckCircle /> };
  };

  const calculateConsumptionRate = (stock) => {
    if (!stock.totalIssued || !stock.allocatedDate) {
      return 'N/A';
    }
    
    const daysSinceAllocation = Math.max(
      1,
      Math.floor((new Date() - new Date(stock.allocatedDate)) / (1000 * 60 * 60 * 24))
    );
    
    const rate = (stock.totalIssued / daysSinceAllocation).toFixed(2);
    return `${rate} units/day`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!Array.isArray(stocks) || stocks.length === 0) {
    return (
      <Alert severity="info">
        No stock allocated to this department yet. Stock will appear here once items are issued to this department.
      </Alert>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Department Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Items: {stocks.length}
            </Typography>
          </Box>

          {/* Stock Statistics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total Quantity
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {Array.isArray(stocks) ? stocks.reduce((sum, stock) => sum + (stock.quantity || 0), 0) : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Low Stock Items
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {Array.isArray(stocks) ? stocks.filter(s => s.minimumLevel && s.quantity <= s.minimumLevel).length : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total Issued
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {Array.isArray(stocks) ? stocks.reduce((sum, stock) => sum + (stock.totalIssued || 0), 0) : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total Returned
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {Array.isArray(stocks) ? stocks.reduce((sum, stock) => sum + (stock.totalReturned || 0), 0) : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Stock Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell align="right">Current Qty</TableCell>
                  <TableCell align="right">Min Level</TableCell>
                  <TableCell align="right">Max Level</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Consumption Rate</TableCell>
                  <TableCell>Last Issued</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stocks.map((stock) => {
                  const status = getStockStatus(stock);
                  return (
                    <TableRow key={stock._id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {stock.item?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stock.item?.itemType || ''} • {stock.item?.unit || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {stock.batchStock?.batchNumber || 'N/A'}
                        </Typography>
                        {stock.batchStock?.expiryDate && (
                          <Typography variant="caption" color="text.secondary">
                            Exp: {new Date(stock.batchStock.expiryDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold">
                          {stock.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {stock.minimumLevel || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {stock.maximumLevel || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          icon={status.icon}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {calculateConsumptionRate(stock)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {stock.lastIssuedDate
                            ? new Date(stock.lastIssuedDate).toLocaleDateString()
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit min/max levels">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(stock)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Stock Levels Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Stock Levels
        </DialogTitle>
        <DialogContent>
          {selectedStock && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Item:</strong> {selectedStock.item?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Quantity:</strong> {selectedStock.quantity}
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Minimum Level"
                    value={editFormData.minimumLevel}
                    onChange={(e) => setEditFormData(prev => ({
                      ...prev,
                      minimumLevel: parseInt(e.target.value) || 0
                    }))}
                    helperText="Alert when stock falls below this level"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Maximum Level"
                    value={editFormData.maximumLevel}
                    onChange={(e) => setEditFormData(prev => ({
                      ...prev,
                      maximumLevel: parseInt(e.target.value) || 0
                    }))}
                    helperText="Alert when stock exceeds this level"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>

              {editFormData.minimumLevel > 0 && editFormData.maximumLevel > 0 && 
               editFormData.minimumLevel >= editFormData.maximumLevel && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Minimum level should be less than maximum level
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={
              editFormData.minimumLevel > 0 && 
              editFormData.maximumLevel > 0 && 
              editFormData.minimumLevel >= editFormData.maximumLevel
            }
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
